import { useNavigate } from '@tanstack/react-router';

import type { Location, Profile } from '../../../entities/post/models';
import { PostFilter } from '../../../entities/post/models';
import { CONVERSATION_PAGE_ANCHOR } from '../conversation/constants';
import { showDrillDownContextMenu } from '../conversation/overlay/show-drill-down-context-menu';
import { useFilteredPostsNavigation } from '../filtered-posts/hook/use-filtered-posts-navigation';
import { useFilteredProfilesNavigation } from '../filtered-profiles/hook/use-filtered-profiles-navigation';
import { PEOPLE_PAGE_ANCHOR } from '../people/constants';
import { MetricsSummary } from './components/metrics-summary';
import { SimpleLocationTable } from './components/simple-location-table';
import { SimpleProfileTable } from './components/simple-profile-table';
import { UnifiedThemeTable } from './components/unified-theme-table';
import { WordCloudSection } from './components/word-cloud-section/index';

import { PUBLIC_FILTER_TYPE } from '@/entities/public-filter/constants';
import { useSelectedSavedQuery } from '@/shared/hooks/use-selected-saved-query';
import { useSharedDateRange } from '@/shared/hooks/use-shared-date-range';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { useRestoreScroll } from '../insight-layout/hook/use-insight-scroll-container';
import { usePublicFilter } from '../shared/public-filter/use-public-filter';

export function OverviewPage() {
  const navigate = useNavigate();
  const { workspaceId } = useWorkspaceId();

  const { query: selectedQuery } = useSelectedSavedQuery();
  const { dateRange } = useSharedDateRange();

  const navigateToConversation = (anchor?: CONVERSATION_PAGE_ANCHOR) => {
    const searchParams = new URLSearchParams(location.search);
    if (anchor) {
      searchParams.set('anchor', anchor);
    }

    navigate({
      to: `/workspace/${workspaceId}/insight/conversation`,
      search: Object.fromEntries(searchParams),
    });
  };

  useRestoreScroll();

  return (
    <div className="space-y-6 p-6">
      <div className="space-y-6">
        <OverviewMetricSection />
        <UnifiedThemeTable
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          queryId={selectedQuery.id}
          onNavigateToConversation={() => navigateToConversation()}
        />
      </div>

      <WordCloudSection
        className="mb-[130px]"
        onNavigateToConversation={() =>
          navigateToConversation(CONVERSATION_PAGE_ANCHOR.WORD_CLOUD_SECTION)
        }
      />
    </div>
  );
}

const OverviewMetricSection = () => {
  const navigate = useNavigate();
  const { workspaceId } = useWorkspaceId();
  const { query } = useSelectedSavedQuery();
  const { filterValues, setFilterValues } = usePublicFilter(query.id);

  const navigateToFilteredPosts = useFilteredPostsNavigation();
  const navigateToFilteredProfiles = useFilteredProfilesNavigation();

  const navigateToPeople = (anchor?: PEOPLE_PAGE_ANCHOR) => {
    const searchParams = new URLSearchParams(location.search);
    if (anchor) {
      searchParams.append('anchor', anchor ?? '');
    }

    navigate({
      to: `/workspace/${workspaceId}/insight/people`,
      search: Object.fromEntries(searchParams),
    });
  };

  return (
    <>
      <MetricsSummary />

      <div
        className="grid grid-cols-2 gap-6"
        style={{
          gridTemplateColumns: 'auto 400px',
        }}
      >
        <SimpleProfileTable
          onProfileClick={async (profile: Profile, event: React.MouseEvent) => {
            const action = await showDrillDownContextMenu({
              position: { x: event.clientX, y: event.clientY },
            });

            switch (action) {
              case 'view-posts':
                await navigateToFilteredPosts({
                  filter: {
                    [PostFilter.Profile]: profile.username,
                  },
                  dimension: PostFilter.Profile,
                });
                break;
              case 'view-profiles':
                await navigateToFilteredProfiles({
                  filter: {
                    [PostFilter.Profile]: profile.username,
                  },
                  dimension: PostFilter.Profile,
                });
                break;
              case 'close':
                break;
            }
          }}
          onNavigate={() => navigateToPeople(PEOPLE_PAGE_ANCHOR.PEOPLE_TABLE)}
          pageSize={5}
        />
        <SimpleLocationTable
          onLocationClick={async (location: Location, event: React.MouseEvent) => {
            const action = await showDrillDownContextMenu({
              position: { x: event.clientX, y: event.clientY },
            });

            switch (action) {
              case 'view-posts': {
                await navigateToFilteredPosts();
                const newFilterValues = [
                  ...filterValues,
                  { filterType: PUBLIC_FILTER_TYPE.LOCATION, value: location.code },
                ];
                setFilterValues(newFilterValues);
                break;
              }

              case 'view-profiles': {
                await navigateToFilteredProfiles();
                const newFilterValues = [
                  ...filterValues,
                  { filterType: PUBLIC_FILTER_TYPE.LOCATION, value: location.code },
                ];
                setFilterValues(newFilterValues);
                break;
              }

              case 'close':
                break;
            }
          }}
          onNavigate={() => navigateToPeople(PEOPLE_PAGE_ANCHOR.LOCATION_TABLE)}
          pageSize={5}
        />
      </div>
    </>
  );
};
